
import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { CheckCircle, Play, Star, ArrowRight, LogOut, ChevronDown, Check, ExternalLink, Images, Rocket, RefreshCcw, ShieldCheck, Clock, Target, Shield, Loader2, Link2 } from "lucide-react";
import { API_ROOT } from "../config";
import SEO from "@/components/seo";
import { trackEvent } from "@/lib/meta-pixel";

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '1x00000000000000000000AA';

const PALETTE = {
  inchworm: '#B1FA63',
  gunmetal: '#243837',
  orange: '#FE7733',
  paleViolet: '#B2A1FF',
  brightSnow: '#FFFFFF',
  americanSilver: '#D1D1D1',
};

/// How It Works Section with Sticky Scroll
const HowItWorksSection = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const steps = [
    {
      title: "Define Brief & Create Submission Link",
      description: "Upload your campaign brief. Our AI structures it for you — edit until it's exactly right, then generate a shareable submission link.",
      images: [
        "/Bild1-1.png",
        "/Bild1-2.png",
        "/Bild1-3.png"
      ]
    },
    {
      title: "Share Submission Link",
      description: "You share the submission link with your creators via your preferred communication channel.",
      images: [
        "/Bild2.png",
      ]
    },
    {
      title: "Creators Self-Check",
      description: "Creators upload their video and get instant, private feedback on how it matches your brief. They can revise and re-check until they're confident — before you ever see it.",
      images: [
        "/Bild3-1.png",
        "/Bild3-2.png",
        "/Bild3-3.png"
      ]
    },
    {
      title: "Review & Approve Submissions",
      description: "View all submissions in one dashboard and see, via the BrandComply score, how well each video matches the brief. So you can focus on creative feedback and request revisions only when needed.",
      images: [
        "/Bild4.png",
      ]
    }
  ];

  // Auto-cycle through images for current step
  useEffect(() => {
    const currentImages = steps[activeStep]?.images || [];
    if (currentImages.length <= 1) return;

    const interval = setInterval(() => {
      setActiveImageIndex((prev) => (prev + 1) % currentImages.length);
    }, 3000); // Change image every 3 seconds

    return () => clearInterval(interval);
  }, [activeStep, steps]);

  // Reset image index when step changes
  useEffect(() => {
    setActiveImageIndex(0);
  }, [activeStep]);

  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current || !contentRef.current) return;

      const section = sectionRef.current;
      const sectionRect = section.getBoundingClientRect();
      const sectionTop = sectionRect.top;
      const sectionHeight = section.offsetHeight;
      const viewportHeight = window.innerHeight;
      
      // Calculate how far we've scrolled through the section
      // Section starts when its top reaches the viewport top
      // Section ends when its bottom leaves the viewport
      const scrollableDistance = sectionHeight - viewportHeight;
      const scrollProgress = Math.max(0, Math.min(1, -sectionTop / scrollableDistance));
      
      // Determine active step based on scroll progress
      const stepIndex = Math.min(steps.length - 1, Math.floor(scrollProgress * steps.length));
      
      if (sectionTop <= 0 && sectionTop > -scrollableDistance) {
        setActiveStep(stepIndex);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [steps.length]);

  return (
    <section 
      id="how-it-works" 
      ref={sectionRef}
      style={{ 
        background: PALETTE.brightSnow,
        height: '300vh' // Creates scroll space for 4 steps
      }}
    >
      <div 
        ref={contentRef}
        className="sticky top-0 h-screen flex items-center pt-20"
        style={{ background: PALETTE.brightSnow }}
      >
        <div className="max-w-7xl mx-auto px-6 w-full">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-4" style={{ color: PALETTE.gunmetal }}>
              From brief to approved —<br />without the back-and-forth.
            </h2>                      
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            
            {/* Left Side - Steps */}
            <div className="flex">
              {/* Progress Bar */}
              <div className="relative mr-6 flex-shrink-0">
                {/* Background Line */}
                <div 
                  className="absolute left-1/2 top-0 bottom-0 w-0.5 -translate-x-1/2"
                  style={{ background: `${PALETTE.americanSilver}40` }}
                />
                {/* Active Progress */}
                <div 
                  className="absolute left-1/2 top-0 w-0.5 -translate-x-1/2 transition-all duration-500"
                  style={{ 
                    background: PALETTE.inchworm,
                    height: `${((activeStep + 1) / steps.length) * 100}%`
                  }}
                />
                {/* Step Indicators */}
                <div className="relative flex flex-col justify-between h-full py-2">
                  {steps.map((_, index) => (
                    <div 
                      key={index}
                      className="w-2.5 h-2.5 rounded-full transition-all duration-300"
                      style={{ 
                        background: index <= activeStep ? PALETTE.inchworm : `${PALETTE.americanSilver}40`,
                        transform: index === activeStep ? 'scale(1.2)' : 'scale(1)'
                      }}
                    />
                  ))}
                </div>
              </div>

              {/* Steps Content */}
              <div className="space-y-6">
                {steps.map((step, index) => (
                  <div 
                    key={index}
                    className="transition-all duration-300"
                    style={{
                      opacity: index === activeStep ? 1 : 0.4
                    }}
                  >
                    <h3 
                      className="text-3xl mb-3 transition-all duration-300"
                      style={{ 
                        color: index === activeStep ? PALETTE.gunmetal : `${PALETTE.gunmetal}60`,
                        fontWeight: index === activeStep ? 700 : 400
                      }}
                    >
                      {step.title}
                    </h3>
                    <p 
                      className="text-base leading-relaxed max-w-md"
                      style={{ 
                        color: index === activeStep ? `${PALETTE.gunmetal}80` : `${PALETTE.gunmetal}50`
                      }}
                    >
                      {step.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Side - Screenshot Slideshow */}
            <div className="relative">
              {/* Main Screenshot Container */}
              <div 
                className="relative rounded-2xl overflow-hidden"
                style={{ 
                  aspectRatio: '16/10',
                  minHeight: '400px',
                  maxHeight: '450px'
                }}
              >
                {/* Screenshot Images - Slideshow */}
                {steps[activeStep]?.images && steps[activeStep].images.length > 0 && (
                  <>
                    {steps[activeStep].images.map((imageSrc, imgIndex) => (
                      <img 
                        key={`${activeStep}-${imgIndex}`}
                        src={imageSrc}
                        alt={`${steps[activeStep].title} - Image ${imgIndex + 1}`}
                        className="absolute inset-0 w-full h-full object-contain transition-opacity duration-700"
                        style={{
                          opacity: imgIndex === activeImageIndex ? 1 : 0
                        }}
                        onError={(e) => {
                          // Hide broken images
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    ))}
                  </>
                )}
              </div>

              {/* Image Indicators (dots) */}
              {steps[activeStep]?.images && steps[activeStep].images.length > 1 && (
                <div className="flex justify-center gap-2 mt-4">
                  {steps[activeStep].images.map((_, imgIndex) => (
                    <button
                      key={imgIndex}
                      onClick={() => setActiveImageIndex(imgIndex)}
                      className="w-2.5 h-2.5 rounded-full transition-all duration-300"
                      style={{
                        background: imgIndex === activeImageIndex ? PALETTE.inchworm : `${PALETTE.americanSilver}60`,
                        transform: imgIndex === activeImageIndex ? 'scale(1.2)' : 'scale(1)'
                      }}
                      aria-label={`View image ${imgIndex + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>

          </div>
        </div>
      </div>
    </section>
  );
};

// Detect platform and extract thumbnail for video URL preview
const getVideoPreview = (url: string): { platform: string; thumbnail: string | null } => {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace('www.', '');

    // YouTube
    if (host.includes('youtube.com') || host.includes('youtu.be')) {
      let videoId = '';
      if (host.includes('youtu.be')) {
        videoId = parsed.pathname.slice(1);
      } else {
        videoId = parsed.searchParams.get('v') || '';
        if (!videoId) {
          const shorts = parsed.pathname.match(/\/shorts\/([^/?]+)/);
          if (shorts) videoId = shorts[1];
        }
      }
      if (videoId) {
        return { platform: 'YouTube', thumbnail: `https://img.youtube.com/vi/${videoId}/mqdefault.jpg` };
      }
      return { platform: 'YouTube', thumbnail: null };
    }

    if (host.includes('tiktok.com')) return { platform: 'TikTok', thumbnail: null };
    if (host.includes('instagram.com')) return { platform: 'Instagram', thumbnail: null };
    if (host.includes('vimeo.com')) return { platform: 'Vimeo', thumbnail: null };
    if (host.includes('facebook.com') || host.includes('fb.watch')) return { platform: 'Facebook', thumbnail: null };
    if (host.includes('twitter.com') || host.includes('x.com')) return { platform: 'X / Twitter', thumbnail: null };

    return { platform: host, thumbnail: null };
  } catch {
    return { platform: '', thumbnail: null };
  }
};

// Hero Section with integrated free video check
const HeroSection = ({ setIsModalOpen }: { setIsModalOpen: (open: boolean) => void }) => {
  const [videoUrl, setVideoUrl] = useState('');
  const [checkEmail, setCheckEmail] = useState('');
  const [checkStep, setCheckStep] = useState<'url' | 'email' | 'success' | 'error'>('url');
  const [checkLoading, setCheckLoading] = useState(false);
  const [checkError, setCheckError] = useState('');

  const isValidUrl = (url: string) => {
    try { const p = new URL(url); return ['http:', 'https:'].includes(p.protocol); } catch { return false; }
  };
  const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleUrlContinue = () => {
    if (!videoUrl.trim()) { setCheckError('Please paste a video URL'); return; }
    if (!isValidUrl(videoUrl.trim())) { setCheckError('Please enter a valid URL (e.g. https://...)'); return; }
    setCheckError('');
    setCheckStep('email');
    trackEvent('InitiateCheckout', { content_name: 'Video URL Submitted' });
  };

  const handleSubmitCheck = async () => {
    if (!checkEmail.trim() || !isValidEmail(checkEmail.trim())) {
      setCheckError('Please enter a valid email address'); return;
    }
    setCheckError('');
    setCheckLoading(true);
    try {
      const res = await fetch(`${API_ROOT}/free-video-check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ video_url: videoUrl.trim(), email: checkEmail.trim().toLowerCase() }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as any).detail || 'Something went wrong. Please try again.');
      }
      setCheckStep('success');
      trackEvent('Lead', { content_name: 'Free Video Check Submitted' });
    } catch (err: any) {
      setCheckError(err.message || 'Failed to submit. Please try again.');
      setCheckStep('error');
    } finally { setCheckLoading(false); }
  };

  const preview = videoUrl.trim() && isValidUrl(videoUrl.trim()) ? getVideoPreview(videoUrl.trim()) : null;

  return (
    <section
      style={{ background: PALETTE.gunmetal }}
      className="text-white"
    >
      <div className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid lg:grid-cols-2 gap-16 items-start">

          {/* Left — Copy + CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-5xl lg:text-6xl font-bold mb-8 leading-tight">
              Every creator video.{' '}
              <span style={{ color: PALETTE.inchworm }}>On-Brief.</span>
              <br />Before it reaches your team.
            </h1>
            <p className="text-lg mb-10 text-white/75 max-w-xl">
            Creators self-check their videos against your brief before submission.
            </p>

            <div className="flex items-center gap-4">
              <Button
                className="font-semibold text-base px-8 py-4 rounded-full hover:scale-105 transition-transform duration-200"
                style={{ background: 'transparent', color: PALETTE.inchworm, border: `2px solid ${PALETTE.inchworm}` }}
                onClick={() => setIsModalOpen(true)}
              >
                Start Free Trial
              </Button>
            </div>
          </motion.div>

          {/* Right — Free Video Check (scoped CSS vars so Tailwind primary = inchworm) */}
          <motion.div
            className="w-full ml-auto"
            style={{ '--primary': '89 94% 68%', '--primary-foreground': '177 22% 18%' } as React.CSSProperties}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >

            {checkStep === 'success' ? (
              <div className="w-full animate-slide-up-fade">
                <div
                  className="rounded-2xl p-8 text-center transition-all duration-300"
                  style={{
                    background: PALETTE.gunmetal,
                    border: `1.5px solid ${PALETTE.inchworm}40`,
                  }}
                >
                  <div
                    className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full"
                    style={{ background: `${PALETTE.inchworm}20` }}
                  >
                    <Check className="h-6 w-6" style={{ color: PALETTE.inchworm }} />
                  </div>
                  <p className="text-lg font-medium" style={{ color: PALETTE.brightSnow }}>We're on it.</p>
                  <p className="mt-2 text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>
                    Results will land in your inbox shortly.
                  </p>
                  <button
                    onClick={() => { setVideoUrl(''); setCheckEmail(''); setCheckStep('url'); setCheckError(''); }}
                    className="mt-4 text-xs font-semibold hover:underline bg-transparent border-none cursor-pointer"
                    style={{ color: PALETTE.inchworm }}
                  >
                    Check another video
                  </button>
                </div>
              </div>
            ) : (
              <div
                className="free-check-card rounded-2xl px-8 py-10 transition-all duration-300"
                style={{
                  background: PALETTE.gunmetal,
                  border: `1.5px solid ${PALETTE.inchworm}`,
                }}
              >
                <div className="mb-8">
                  <h3 className="text-3xl font-bold" style={{ color: PALETTE.inchworm }}>Paste a creator video. <br /> See what's off-brief.</h3>
                  <p className="mt-3 text-base" style={{ color: 'rgba(255,255,255,0.55)', lineHeight: 1.6 }}>
                    Paste a public video URL — we'll check it against a best-practice brief and email you the results.
                  </p>
                </div>

                {/* URL Input */}
                <form onSubmit={(e) => { e.preventDefault(); handleUrlContinue(); }}>
                  <div
                    className="relative rounded-xl transition-all duration-300"
                    style={{
                      background: checkStep === 'email' ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.08)',
                      border: checkStep === 'email' ? `1px solid ${PALETTE.inchworm}40` : '1px solid rgba(255,255,255,0.12)',
                    }}
                  >
                    <div className="flex items-center gap-3 px-5 py-4">
                      <Link2 className="h-5 w-5 shrink-0" style={{ color: checkStep === 'email' ? PALETTE.inchworm : 'rgba(255,255,255,0.45)' }} />
                      <input
                        type="url"
                        value={videoUrl}
                        onChange={(e) => { setVideoUrl(e.target.value); setCheckError(''); }}
                        disabled={checkStep === 'email'}
                        placeholder="Paste a TikTok, Instagram, YouTube URL…"
                        className="w-full bg-transparent text-base focus:outline-none disabled:cursor-default disabled:opacity-70"
                        style={{ color: PALETTE.brightSnow }}
                      />
                      {checkStep !== 'email' && videoUrl.trim().length > 5 && (
                        <button
                          type="submit"
                          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-all duration-150 hover:brightness-110 active:scale-95"
                          style={{ background: PALETTE.inchworm, color: PALETTE.gunmetal }}
                        >
                          <ArrowRight className="h-4 w-4" />
                        </button>
                      )}
                      {checkStep === 'email' && (
                        <button
                          type="button"
                          onClick={() => { setCheckStep('url'); setCheckError(''); }}
                          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
                          style={{ background: `${PALETTE.inchworm}25` }}
                          title="Change URL"
                        >
                          <Check className="h-3.5 w-3.5" style={{ color: PALETTE.inchworm }} />
                        </button>
                      )}
                    </div>
                  </div>
                </form>

                {/* Video Preview */}
                {preview && (
                  <div className="mt-4 overflow-hidden flex items-center gap-3">
                    {preview.thumbnail ? (
                      <>
                        <img
                          src={preview.thumbnail}
                          alt="Video thumbnail"
                          className="flex-shrink-0 object-cover rounded-lg"
                          style={{ width: 96, height: 54 }}
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                        <div className="min-w-0">
                          <p className="text-xs font-semibold" style={{ color: PALETTE.inchworm }}>{preview.platform}</p>
                          <p className="text-xs truncate" style={{ color: 'rgba(255,255,255,0.5)' }}>{videoUrl.trim()}</p>
                        </div>
                      </>
                    ) : (
                      <>
                        <div
                          className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ background: 'rgba(255,255,255,0.08)' }}
                        >
                          <Play className="w-4 h-4" style={{ color: PALETTE.inchworm }} />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-semibold" style={{ color: PALETTE.inchworm }}>{preview.platform}</p>
                          <p className="text-xs truncate" style={{ color: 'rgba(255,255,255,0.5)' }}>{videoUrl.trim()}</p>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Email Input */}
                {checkStep === 'email' && (
                  <form onSubmit={(e) => { e.preventDefault(); handleSubmitCheck(); }} className="mt-4 animate-slide-up-fade">
                    <div
                      className="relative rounded-xl transition-all duration-300 animate-pulse-ring"
                      style={{
                        background: 'rgba(255,255,255,0.08)',
                        border: `1px solid ${PALETTE.inchworm}40`,
                      }}
                    >
                      <div className="flex items-center gap-3 px-5 py-4">
                        <span className="text-base shrink-0" style={{ color: 'rgba(255,255,255,0.45)' }}>@</span>
                        <input
                          type="email"
                          value={checkEmail}
                          onChange={(e) => { setCheckEmail(e.target.value); setCheckError(''); }}
                          autoFocus
                          placeholder="Your email for the results"
                          className="w-full bg-transparent text-base focus:outline-none"
                          style={{ color: PALETTE.brightSnow }}
                        />
                        {checkEmail.includes('@') && checkEmail.includes('.') && (
                          <button
                            type="submit"
                            disabled={checkLoading}
                            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-all duration-150 hover:brightness-110 active:scale-95"
                            style={{ background: PALETTE.inchworm, color: PALETTE.gunmetal }}
                          >
                            {checkLoading ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <ArrowRight className="h-4 w-4" />
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  </form>
                )}

                {/* Error */}
                {(checkError || checkStep === 'error') && (
                  <div
                    className="text-xs text-center p-2.5 rounded-lg mt-4"
                    style={{ color: PALETTE.orange, background: `${PALETTE.orange}15`, border: `1px solid ${PALETTE.orange}30` }}
                  >
                    {checkError}
                    {checkStep === 'error' && (
                      <button
                        onClick={() => { setCheckStep('email'); setCheckError(''); }}
                        className="block mx-auto mt-1 text-xs font-semibold hover:underline bg-transparent border-none cursor-pointer"
                        style={{ color: PALETTE.orange }}
                      >
                        Try again
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </motion.div>
        </div>
      </div>

      <style>{`
        @keyframes slide-up-fade {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-slide-up-fade {
          animation: slide-up-fade 0.4s ease forwards;
        }
        @keyframes pulse-ring {
          0%, 100% { box-shadow: 0 0 0 0 rgba(177,250,99,0); }
          50% { box-shadow: 0 0 0 4px rgba(177,250,99,0.1); }
        }
        .animate-pulse-ring {
          animation: pulse-ring 2s ease-in-out infinite;
        }
        @keyframes glow-breathe {
          0%, 100% { box-shadow: 0 0 12px 2px rgba(177,250,99,0.10), 0 0 4px 0px rgba(177,250,99,0.08); }
          50% { box-shadow: 0 0 28px 6px rgba(177,250,99,0.22), 0 0 10px 2px rgba(177,250,99,0.14); }
        }
        .free-check-card {
          animation: glow-breathe 3.5s ease-in-out infinite;
        }
        .free-check-card:hover {
          animation: none;
          box-shadow: 0 0 36px 8px rgba(177,250,99,0.30), 0 0 12px 2px rgba(177,250,99,0.18);
        }
      `}</style>
    </section>
  );
};

const Auth = () => {
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [surname, setSurname] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoginMode, setIsLoginMode] = useState(false);
  const [userType] = useState<'brand'>('brand'); // Always brand, no distinction shown
  const [openFaqItems, setOpenFaqItems] = useState<{ [key: number]: boolean }>({});
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isForgotPasswordMode, setIsForgotPasswordMode] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordError, setForgotPasswordError] = useState("");
  const navigate = useNavigate();
  
  // Cloudflare Turnstile state
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const turnstileWrapperRef = useRef<HTMLDivElement | null>(null);
  const [turnstileScale, setTurnstileScale] = useState({ x: 1, y: 1 });
  const TURNSTILE_CONTAINER_ID = 'turnstile-container';
  const TURNSTILE_BASE_WIDTH = 300;
  const TURNSTILE_BASE_HEIGHT = 65;

  const updateTurnstileScale = useCallback(() => {
    const wrapper = turnstileWrapperRef.current;
    if (!wrapper) return;
    const width = wrapper.getBoundingClientRect().width;
    if (!width) return;
    const rawScale = Math.min(width / TURNSTILE_BASE_WIDTH, 1); // Cap at 1 (original size)
    setTurnstileScale({ x: rawScale, y: rawScale }); // Keep same scale for both dimensions
  }, []);

  useEffect(() => {
    document.body.classList.add('landing-page');
    return () => { document.body.classList.remove('landing-page'); };
  }, []);


  // Load Turnstile script on mount
  useEffect(() => {
    if (document.getElementById('turnstile-script')) return;
    const script = document.createElement('script');
    script.id = 'turnstile-script';
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    script.async = true;
    document.head.appendChild(script);
  }, []);

  // Configure Silktide Cookie Banner
  useEffect(() => {
    const configureCookieBanner = () => {
      const silktide = (window as any).silktideCookieBannerManager;
      if (!silktide) {
        // Retry if script hasn't loaded yet
        setTimeout(configureCookieBanner, 100);
        return;
      }

      silktide.updateCookieBannerConfig({
        background: {
          showBackground: false
        },
        cookieIcon: {
          position: "bottomRight",
          size: "small"
        },
        cookieTypes: [
          {
            id: "necessary",
            name: "Necessary",
            description: "<p>These cookies are necessary for the website to function properly and cannot be switched off. They help with things like logging in and setting your privacy preferences.</p>",
            required: true,
            onAccept: function() {
              console.log('Necessary cookies accepted');
            }
          },
          {
            id: "analytics",
            name: "Analytics",
            description: "<p>These cookies help us improve the site by tracking which pages are most popular and how visitors move around the site.</p>",
            required: false,
            onAccept: function() {
              const gtag = (window as any).gtag;
              const dataLayer = (window as any).dataLayer;
              if (gtag) {
                gtag('consent', 'update', {
                  analytics_storage: 'granted',
                });
              }
              if (dataLayer) {
                dataLayer.push({
                  'event': 'consent_accepted_analytics',
                });
              }
            },
            onReject: function() {
              const gtag = (window as any).gtag;
              if (gtag) {
                gtag('consent', 'update', {
                  analytics_storage: 'denied',
                });
              }
            }
          }
        ],
        text: {
          banner: {
            description: "<p>We use cookies on our site to enhance your user experience, provide personalized content, and analyze our traffic. <a href=\"/privacy-policy\" target=\"_blank\">Cookie Policy.</a></p>",
            acceptAllButtonText: "Accept all",
            acceptAllButtonAccessibleLabel: "Accept all cookies",
            rejectNonEssentialButtonText: "Reject non-essential",
            rejectNonEssentialButtonAccessibleLabel: "Reject non-essential",
            preferencesButtonText: "Preferences",
            preferencesButtonAccessibleLabel: "Toggle preferences"
          },
          preferences: {
            title: "Customize your cookie preferences",
            description: "<p>We respect your right to privacy. You can choose not to allow some types of cookies. Your cookie preferences will apply across our website.</p>",
            creditLinkText: "Get this banner for free",
            creditLinkAccessibleLabel: "Get this banner for free"
          }
        },
        position: {
          banner: "bottomCenter"
        }
      });
    };

    configureCookieBanner();
  }, []);

  // Render widget when modal opens
  useEffect(() => {
    if (!isModalOpen) return;
    
    // Reset token
    setTurnstileToken(null);
    
    const renderWidget = () => {
      const turnstile = (window as any).turnstile;
      const container = document.getElementById(TURNSTILE_CONTAINER_ID);
      
      if (!turnstile || !container) {
        // Retry until script loads and container exists
        setTimeout(renderWidget, 200);
        return;
      }
      
      // Remove old widget if exists
      if (widgetIdRef.current) {
        try { turnstile.remove(widgetIdRef.current); } catch {}
        widgetIdRef.current = null;
      }
      container.innerHTML = '';
      
      // Render new widget
      widgetIdRef.current = turnstile.render(`#${TURNSTILE_CONTAINER_ID}`, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token: string) => setTurnstileToken(token),
        'expired-callback': () => setTurnstileToken(null),
        theme: 'light',
      });
      updateTurnstileScale();
    };
    
    // Delay to ensure modal DOM is rendered
    setTimeout(renderWidget, 300);
  }, [isModalOpen, isLoginMode, isForgotPasswordMode, updateTurnstileScale]);

  useEffect(() => {
    if (!isModalOpen) return;
    updateTurnstileScale();
    const handleResize = () => updateTurnstileScale();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isModalOpen, updateTurnstileScale]);

  // Reset function for mode switching
  const resetTurnstile = useCallback(() => {
    setTurnstileToken(null);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      // Verify Turnstile token
      if (!turnstileToken) {
        setError("Please complete the verification challenge.");
        setIsLoading(false);
        return;
      }

      const emailNorm = email.trim().toLowerCase();

      // Validate required fields for registration
      if (!isLoginMode) {
        const firstNameTrimmed = firstName.trim();
        const surnameTrimmed = surname.trim();
        const companyNameTrimmed = companyName.trim();
        
        if (!companyNameTrimmed) {
          setError("Company name is required");
          setIsLoading(false);
          return;
        }
        
        if (!firstNameTrimmed) {
          setError("First name is required");
          setIsLoading(false);
          return;
        }
        
        if (firstNameTrimmed.length < 2) {
          setError("First name must be at least 2 characters");
          setIsLoading(false);
          return;
        }
        
        if (!surnameTrimmed) {
          setError("Surname is required");
          setIsLoading(false);
          return;
        }
        
        if (surnameTrimmed.length < 2) {
          setError("Surname must be at least 2 characters");
          setIsLoading(false);
          return;
        }
        
        if (!emailNorm) {
          setError("Email is required");
          setIsLoading(false);
          return;
        }
        
        if (!password.trim()) {
          setError("Password is required");
          setIsLoading(false);
          return;
        }
      }

      if (isLoginMode) {
        // --- LOGIN LOGIC ---
        const loginParams = new URLSearchParams();
        loginParams.append("username", emailNorm);
        loginParams.append("password", password);
        loginParams.append("turnstile_token", turnstileToken);

        const res = await fetch(`${API_ROOT}/token`, {
          method: "POST",
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: loginParams,
        });

        if (!res.ok) {
          const data = await res.json().catch(() => ({} as any));
          if (res.status === 403) {
            throw new Error("Email not verified. Please check your email and click the verification link, or register again for a new verification email.");
          }
          throw new Error(data.detail || "Login failed. Please check your credentials.");
        }

        const tokenData = await res.json();
        localStorage.setItem("token", tokenData.access_token);
        localStorage.setItem("userType", userType);
        
        // Always redirect to brand campaign page
        navigate("/brand/campaign");

      } else {
        // --- REGISTRATION LOGIC ---
        const formData = new FormData();
        formData.append("first_name", firstName.trim());
        formData.append("surname", surname.trim());
        formData.append("email", emailNorm);
        formData.append("password", password.trim());
        formData.append("user_type", userType);
        formData.append("company_name", companyName.trim());
        formData.append("turnstile_token", turnstileToken);
        // Pass the current frontend origin so the email verification link
        // uses the correct Cloudflare preview / environment URL
        if (typeof window !== "undefined") {
          formData.append("frontend_url", window.location.origin);
        }

        const res = await fetch(`${API_ROOT}/register`, {
          method: "POST",
          body: formData,
        });

        if (!res.ok) {
          const data = await res.json().catch(() => ({} as any));
          throw new Error(data.detail || "Registration failed. Please try again.");
        }

        // Registration successful - close form modal and show success modal
        setIsModalOpen(false);
        setShowSuccessModal(true);
        setIsLoading(false);
        return;
      }
    } catch (err: any) {
      setError(err.message || "An unexpected error occurred.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setForgotPasswordError("");
    setForgotPasswordLoading(true);

    try {
      if (!turnstileToken) {
        setForgotPasswordError("Please complete the verification challenge.");
        setForgotPasswordLoading(false);
        return;
      }

      const emailNorm = forgotPasswordEmail.trim().toLowerCase();
      if (!emailNorm) {
        setForgotPasswordError("Please enter your email address.");
        setForgotPasswordLoading(false);
        return;
      }

      const formData = new FormData();
      formData.append("email", emailNorm);
      formData.append("turnstile_token", turnstileToken);
      if (typeof window !== "undefined") {
        formData.append("frontend_url", window.location.origin);
      }

      const res = await fetch(`${API_ROOT}/forgot-password`, {
        method: "POST",
        body: formData,
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({} as any));
        throw new Error(data.detail || "Something went wrong. Please try again.");
      }

      setForgotPasswordSent(true);
    } catch (err: any) {
      setForgotPasswordError(err.message || "An unexpected error occurred.");
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const openModal = (loginMode: boolean) => {
    setIsLoginMode(loginMode);
    setIsForgotPasswordMode(false);
    setForgotPasswordSent(false);
    setForgotPasswordEmail("");
    setForgotPasswordError("");
    setIsModalOpen(true);
    setError("");
    setEmail("");
    setPassword("");
    setFirstName("");
    setSurname("");
    setCompanyName("");
    setIsLoading(false);
  };

  const toggleFaqItem = (index: number) => {
    setOpenFaqItems(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  return (
    <>
      <SEO
        title="AI-powered content approval for Influencer and UGC video campaigns."
        description="BrandComply lets creators self-check their videos against your campaign brief before submission. Launch influencer and UGC campaigns faster, more consistently, and on-brief."
        path="/"
      />
      <div className="min-h-screen bg-brightSnow" style={{ background: PALETTE.brightSnow }}>
        {/* Navbar */}
        <header style={{ background: PALETTE.gunmetal }} className="text-white sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              {/* Logo - Left Side */}
                <div className="flex items-center gap-8">
                  <div
                    className="flex items-center space-x-1 cursor-pointer"
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  >
                    <img src="/brandcomply_logo.png" alt="Brand Logo" className="h-6 w-auto" />
                    <div className="text-xl font-bold" style={{ color: PALETTE.inchworm }}>
                      BrandComply
                    </div>
                  </div>

                  {/* Left-aligned Navigation */}
                  <div className="flex items-center gap-8">
                    <button
                      onClick={() => {
                        const howItWorksSection = document.getElementById('how-it-works');
                        howItWorksSection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }}
                      className="text-sm font-bold hover:opacity-70 transition-opacity"
                      style={{ color: PALETTE.inchworm}}
                    >
                      How it Works
                    </button>

                    <button
                      onClick={() => {
                        const pricingSection = document.getElementById('pricing');
                        pricingSection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }}
                      className="text-sm font-bold hover:opacity-70 transition-opacity"
                      style={{ color: PALETTE.inchworm}}
                    >
                      Pricing
                    </button>

                    <button
                      onClick={() => navigate('/api')}
                      className="text-sm font-bold hover:opacity-70 transition-opacity"
                      style={{ color: PALETTE.inchworm }}
                    >
                      API
                    </button>
                  </div>
                </div>

              {/* Auth Buttons - Right Side */}
              <div className="flex items-center gap-3">
                <Button 
                  className="font-semibold w-fit rounded-full h-8 px-4 hover:scale-105 transition-transform transition-colors duration-200"
                  style={{ background: 'transparent', color: PALETTE.brightSnow, border: `1px solid ${PALETTE.brightSnow}`, borderRadius: '9999px' }}
                  onClick={() => openModal(true)}
                >
                  Sign In
                </Button>
                <Button 
                  className="font-semibold w-fit rounded-full h-8 px-4 hover:scale-105 transition-transform transition-colors duration-200"
                  style={{ background: PALETTE.inchworm, color: PALETTE.gunmetal }}
                  onClick={() => openModal(false)}
                >
                  Sign Up
                </Button>
              </div>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <HeroSection setIsModalOpen={setIsModalOpen} />

        <Dialog open={isModalOpen} onOpenChange={(open) => {
          setIsModalOpen(open);
          if (!open) {
            setIsForgotPasswordMode(false);
            setForgotPasswordSent(false);
            setForgotPasswordError("");
          }
        }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-center text-2xl font-bold mb-2" style={{ color: PALETTE.gunmetal }}>
                {isForgotPasswordMode 
                  ? (forgotPasswordSent ? "Check Your Email" : "Reset Password")
                  : (isLoginMode ? "Welcome Back" : "Start now")}
              </DialogTitle>
              <p className="text-center text-base" style={{ color: `${PALETTE.gunmetal}80` }} id="auth-dialog-description">
                {isForgotPasswordMode 
                  ? (forgotPasswordSent 
                    ? "We've sent you a reset link" 
                    : "Enter your email to receive a reset link")
                  : (isLoginMode 
                    ? "Sign in to your account" 
                    : "Create your account")}
              </p>
            </DialogHeader>

            <div aria-describedby="auth-dialog-description">
              {isForgotPasswordMode ? (
                forgotPasswordSent ? (
                  /* Forgot Password - Success State */
                  <div className="space-y-4">
                    <div 
                      className="p-4 rounded-lg text-center"
                      style={{ background: `${PALETTE.inchworm}10`, border: `1px solid ${PALETTE.inchworm}30` }}
                    >
                      <p className="text-sm" style={{ color: PALETTE.gunmetal }}>
                        If an account exists for <strong>{forgotPasswordEmail}</strong>, you'll receive a password reset link shortly. Please check your inbox and spam folder.
                      </p>
                    </div>
                    <div 
                      className="p-4 rounded-lg text-left"
                      style={{ background: `${PALETTE.inchworm}10`, border: `1px solid ${PALETTE.inchworm}30` }}
                    >
                      <p className="text-sm" style={{ color: PALETTE.gunmetal }}>
                        <strong>Note:</strong> The reset link expires in 30 minutes.
                      </p>
                    </div>
                    <Button
                      onClick={() => {
                        setIsForgotPasswordMode(false);
                        setForgotPasswordSent(false);
                        setForgotPasswordEmail("");
                        setForgotPasswordError("");
                        setIsLoginMode(true);
                        resetTurnstile();
                      }}
                      className="w-full h-12 rounded-full font-semibold text-base transition-all duration-200 hover:scale-105"
                      style={{ 
                        background: `linear-gradient(135deg, ${PALETTE.inchworm}, ${PALETTE.inchworm}e6)`,
                        color: PALETTE.gunmetal,
                        border: 'none',
                        borderRadius: '9999px'
                      }}
                    >
                      Back to Sign In
                    </Button>
                  </div>
                ) : (
                  /* Forgot Password - Email Form */
                  <form onSubmit={handleForgotPassword} className="space-y-4">
                    <div>
                      <label className="text-sm font-medium block mb-2" style={{ color: PALETTE.gunmetal }}>
                        Email Address
                      </label>
                      <Input
                        type="email"
                        placeholder="Enter your email"
                        value={forgotPasswordEmail}
                        onChange={(e) => setForgotPasswordEmail(e.target.value)}
                        required
                        className="h-12 border-2 rounded-xl transition-all duration-200"
                        style={{ borderColor: `${PALETTE.americanSilver}60`, background: PALETTE.brightSnow }}
                      />
                    </div>

                    {/* Cloudflare Turnstile Widget */}
                    <div className="w-full">
                      <div
                        ref={turnstileWrapperRef}
                        className="w-full flex justify-center"
                        style={{
                          height: Math.ceil(TURNSTILE_BASE_HEIGHT * turnstileScale.y),
                          overflow: 'hidden'
                        }}
                      >
                        <div
                          id="turnstile-container"
                          style={{
                            transform: `scale(${turnstileScale.x}, ${turnstileScale.y})`,
                            transformOrigin: 'top center',
                            width: `${TURNSTILE_BASE_WIDTH}px`,
                            height: `${TURNSTILE_BASE_HEIGHT}px`
                          }}
                        />
                      </div>
                    </div>

                    {forgotPasswordError && (
                      <div 
                        className="text-sm text-center p-3 rounded-lg" 
                        style={{ 
                          color: PALETTE.orange,
                          background: `${PALETTE.orange}10`,
                          border: `1px solid ${PALETTE.orange}20`
                        }}
                      >
                        {forgotPasswordError}
                      </div>
                    )}

                    <Button
                      type="submit"
                      className="w-full h-12 rounded-full font-semibold text-base transition-all duration-200 hover:scale-105"
                      style={{ 
                        background: `linear-gradient(135deg, ${PALETTE.inchworm}, ${PALETTE.inchworm}e6)`,
                        color: PALETTE.gunmetal,
                        border: 'none',
                        borderRadius: '9999px'
                      }}
                      disabled={forgotPasswordLoading}
                    >
                      {forgotPasswordLoading ? "Sending..." : "Send Reset Link"}
                    </Button>

                    <p className="text-center text-sm" style={{ color: `${PALETTE.gunmetal}80` }}>
                      Remember your password?{" "}
                      <button 
                        type="button" 
                        className="font-semibold hover:underline"
                        style={{ color: PALETTE.inchworm }}
                        onClick={() => {
                          setIsForgotPasswordMode(false);
                          setForgotPasswordError("");
                          setIsLoginMode(true);
                          resetTurnstile();
                        }}
                      >
                        Sign in
                      </button>
                    </p>
                  </form>
                )
              ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {!isLoginMode && (
                  <>
                    <div>
                      <label className="text-sm font-medium block mb-2" style={{ color: PALETTE.gunmetal }}>
                        Company Name
                      </label>
                      <Input
                        type="text"
                        placeholder="Enter your company name"
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        required
                        className="h-12 border-2 rounded-xl transition-all duration-200"
                        style={{ borderColor: `${PALETTE.americanSilver}60`, background: PALETTE.brightSnow }}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-sm font-medium block mb-2" style={{ color: PALETTE.gunmetal }}>
                          First Name
                        </label>
                        <Input
                          type="text"
                          placeholder="First name"
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          required
                          className="h-12 border-2 rounded-xl transition-all duration-200"
                          style={{ borderColor: `${PALETTE.americanSilver}60`, background: PALETTE.brightSnow }}
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium block mb-2" style={{ color: PALETTE.gunmetal }}>
                          Surname
                        </label>
                        <Input
                          type="text"
                          placeholder="Surname"
                          value={surname}
                          onChange={(e) => setSurname(e.target.value)}
                          required
                          className="h-12 border-2 rounded-xl transition-all duration-200"
                          style={{ borderColor: `${PALETTE.americanSilver}60`, background: PALETTE.brightSnow }}
                        />
                      </div>
                    </div>
                  </>
                )}

                <div>
                  <label className="text-sm font-medium block mb-2" style={{ color: PALETTE.gunmetal }}>
                    Email Address
                  </label>
                  <Input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-12 border-2 rounded-xl transition-all duration-200"
                    style={{ borderColor: `${PALETTE.americanSilver}60`, background: PALETTE.brightSnow }}
                  />
                </div>

                <div>
                  <label className="text-sm font-medium block mb-2" style={{ color: PALETTE.gunmetal }}>
                    Password
                  </label>
                  <Input
                    type="password"
                    placeholder={isLoginMode ? "Enter your password" : "Create a password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="h-12 border-2 rounded-xl transition-all duration-200"
                    style={{ borderColor: `${PALETTE.americanSilver}60`, background: PALETTE.brightSnow }}
                  />
                  {isLoginMode && (
                    <div className="text-right mt-1">
                      <button
                        type="button"
                        className="text-sm font-medium hover:underline"
                        style={{ color: `${PALETTE.gunmetal}80` }}
                        onClick={() => {
                          setIsForgotPasswordMode(true);
                          setForgotPasswordEmail(email);
                          setForgotPasswordError("");
                          setForgotPasswordSent(false);
                          setError("");
                          resetTurnstile();
                        }}
                      >
                        Forgot password?
                      </button>
                    </div>
                  )}
                </div>

                {/* Cloudflare Turnstile Widget */}
                <div className="w-full">
                  <div
                    ref={turnstileWrapperRef}
                    className="w-full flex justify-center"
                    style={{
                      height: Math.ceil(TURNSTILE_BASE_HEIGHT * turnstileScale.y),
                      overflow: 'hidden'
                    }}
                  >
                    <div
                      id="turnstile-container"
                      style={{
                        transform: `scale(${turnstileScale.x}, ${turnstileScale.y})`,
                        transformOrigin: 'top center',
                        width: `${TURNSTILE_BASE_WIDTH}px`,
                        height: `${TURNSTILE_BASE_HEIGHT}px`
                      }}
                    />
                  </div>
                </div>

                {error && (
                  <div 
                    className="text-sm text-center p-3 rounded-lg" 
                    style={{ 
                      color: error.includes("check your email") ? PALETTE.gunmetal : PALETTE.orange,
                      background: error.includes("check your email") ? `${PALETTE.inchworm}10` : `${PALETTE.orange}10`,
                      border: `1px solid ${error.includes("check your email") ? PALETTE.inchworm : PALETTE.orange}20`
                    }}
                  >
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full h-12 rounded-full font-semibold text-base transition-all duration-200 hover:scale-105"
                  style={{ 
                    background: `linear-gradient(135deg, ${PALETTE.inchworm}, ${PALETTE.inchworm}e6)`,
                    color: PALETTE.gunmetal,
                    border: 'none',
                    borderRadius: '9999px'
                  }}
                  disabled={isLoading}
                >
                  {isLoading ? (isLoginMode ? "Signing In..." : "Creating Account...") : (isLoginMode ? "Sign In" : "Sign Up")}
                </Button>

                <p className="text-xs text-center" style={{ color: `${PALETTE.gunmetal}60` }}>
                  By {isLoginMode ? "signing in" : "signing up"}, you agree to our Terms of Service and Privacy Policy
                </p>

                <p className="text-center text-sm" style={{ color: `${PALETTE.gunmetal}80` }}>
                  {isLoginMode ? "Don't have an account?" : "Already have an account?"}{" "}
                  <button 
                    type="button" 
                    className="font-semibold hover:underline"
                    style={{ color: PALETTE.inchworm }}
                    onClick={() => {
                      setIsLoginMode(!isLoginMode);
                      setError("");
                      setIsLoading(false);
                      resetTurnstile();
                    }}
                  >
                    {isLoginMode ? "Sign up" : "Sign in"}
                  </button>
                </p>
              </form>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Success Modal - Email Verification Sent */}
        <Dialog open={showSuccessModal} onOpenChange={setShowSuccessModal}>
          <DialogContent className="max-w-lg">
            <div className="text-center py-6">         
              {/* Title */}
              <h2 className="text-3xl font-bold mb-4" style={{ color: PALETTE.gunmetal }}>
                Check Your Email!
              </h2>

              {/* Message */}
              <p className="text-base mb-6 leading-relaxed" style={{ color: `${PALETTE.gunmetal}80` }}>
                We've sent a verification link to your email address. 
                <br />
                <strong>Please check your inbox (and spam folder)</strong> and click the link to activate your account.
              </p>

              {/* Trial Info */}
              <div 
                className="p-4 rounded-xl mb-4 text-left"
                style={{ background: `${PALETTE.inchworm}10`, border: `1px solid ${PALETTE.inchworm}30` }}
              >
                <p className="text-sm font-semibold mb-2" style={{ color: PALETTE.gunmetal }}>
                  Your 14-day free trial starts after verification:
                </p>
                <ul className="text-sm space-y-1" style={{ color: `${PALETTE.gunmetal}CC` }}>
                  <li>✓ 30 video checks included</li>
                  <li>✓ Full access to all features</li>
                  <li>✓ No credit card required</li>
                </ul>
              </div>

              <p className="text-xs mb-6" style={{ color: `${PALETTE.gunmetal}60` }}>
                The verification link expires in 30 minutes.
              </p>

              {/* Close Button */}
              <Button
                onClick={() => setShowSuccessModal(false)}
                className="w-full h-12 rounded-full font-semibold text-base"
                style={{ 
                  background: PALETTE.inchworm, 
                  color: PALETTE.gunmetal 
                }}
              >
                Got it!
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* ... keep existing code (all the sections from Social Proof to Footer) */}

        {/* Social Proof */}
        
        <div className="max-w-7xl mx-auto"> 

        {/* Benefits */}
        <section id="features" className="py-16" style={{ background: PALETTE.brightSnow }}>
          <div className="max-w-7xl mx-auto px-6">
            {/* Centered Section Header */}
            <motion.div
              className="text-center mb-12 max-w-4xl mx-auto"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-5xl font-bold mb-4" style={{ color: PALETTE.gunmetal }}>
              Your team reviews less. Your creators submit better. Every time.
              </h2>
            </motion.div>    

            {/* Three Benefit Elements */}
            <div className="grid md:grid-cols-3 gap-8 items-stretch">
              {[
                {
                  title: "Get Hours Back Every Week.",
                  description: "Your team spends hours every week on revision emails. BrandComply lets creators self-check their videos against your brief before submitting — so most issues are fixed before you ever hit play.",
                  icon: <RefreshCcw className="w-8 h-8" style={{ color: PALETTE.gunmetal }} />
                },
                {
                  title: "Same Brief. Same Standards. Every Creator.",
                  description: "Whether you're working with 5 creators or 50 — every video gets checked against the same briefing, the same way. No subjective reviews, no inconsistency between reviewers. Creators keep their style, you keep your standards.",
                  icon: <Target className="w-8 h-8" style={{ color: PALETTE.gunmetal }} />
                },
                {
                  title: "Nothing Goes Live Off-Brief.",
                  description: "Missing ad or sponsorship disclosures, wrong CTAs, forgotten talking points — BrandComply catches them before your client ever sees the video. Every submission gets a compliance score so your team knows exactly what needs attention.",
                  icon: <Shield className="w-8 h-8" style={{ color: PALETTE.gunmetal }} />
                }
              ].map((benefit, index) => (
                <motion.div 
                  key={index}
                  className="p-10 bg-white rounded-3xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-300 flex flex-col"
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.15 }}
                >
                  <div 
                    className="w-16 h-16 rounded-2xl flex items-center justify-center mb-8"
                    style={{ background: PALETTE.inchworm, color: PALETTE.gunmetal }}
                  >
                    {benefit.icon}
                  </div>
                  <h3 className="text-2xl font-bold mb-4" style={{ color: PALETTE.gunmetal }}>
                    {benefit.title}
                  </h3>
                  <p className="text-lg leading-relaxed" style={{ color: `${PALETTE.gunmetal}70` }}>
                    {benefit.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>


        {/* How it Works - Sticky Scroll Section */}
        <HowItWorksSection />

        {/* Pricing Section */}
        <section id="pricing" className="py-16" style={{ background: PALETTE.brightSnow }}>
          <div className="max-w-7xl mx-auto px-6">
            <motion.div
              className="text-center mb-12"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
            <h2 className="text-5xl font-bold mb-4" style={{ color: PALETTE.gunmetal }}>
              Flexible Plans for Every Stage.
            </h2>            
            </motion.div>

            {/* Pricing Cards */}
            <div className="grid md:grid-cols-3 gap-6 max-w-7xl mx-auto items-stretch">
              {/* Starter Plan Card */}
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0 }}
              >
              <Card className="w-full h-full border-2 hover:border-opacity-100 transition-all duration-300 flex flex-col" 
                style={{ borderColor: `${PALETTE.americanSilver}60` }}>
                <CardContent className="p-8 flex flex-col flex-1">
                  {/* Plan Header */}
                  <div className="text-center mb-6">
                    <h2 className="text-2xl font-bold mb-1" style={{ color: PALETTE.gunmetal }}>
                      Starter
                    </h2>                
                  </div>

                  {/* Price */}
                  <div className="text-center mb-6">
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-5xl font-bold" style={{ color: PALETTE.gunmetal }}>
                        €29
                      </span>
                      <span className="text-lg" style={{ color: `${PALETTE.gunmetal}60` }}>
                        /month
                      </span>
                    </div>
                    <p className="text-sm mt-2" style={{ color: `${PALETTE.gunmetal}70` }}>
                      For freelancers & small marketing teams
                    </p>
                  </div>

              {/* Features */}
              <ul className="space-y-4 mb-8 flex-1">
                <li className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5"
                    style={{ background: PALETTE.inchworm }}>
                    <Check className="w-3 h-3" style={{ color: PALETTE.gunmetal }} strokeWidth={3} />
                  </div>
                  <span style={{ color: PALETTE.gunmetal }}>
                    <strong>30</strong> video checks per month
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5"
                    style={{ background: PALETTE.inchworm }}>
                    <Check className="w-3 h-3" style={{ color: PALETTE.gunmetal }} strokeWidth={3} />
                  </div>
                  <span style={{ color: PALETTE.gunmetal }}>
                    Unlimited campaigns
                  </span>
                </li>                
                <li className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5"
                    style={{ background: PALETTE.inchworm }}>
                    <Check className="w-3 h-3" style={{ color: PALETTE.gunmetal }} strokeWidth={3} />
                  </div>
                  <span style={{ color: PALETTE.gunmetal }}>
                    Unlimited shareable submission links
                  </span>
                </li>          
              </ul>

              {/* CTA Button */}
              <Button
                onClick={() => openModal(false)}
                className="w-full h-12 rounded-full text-base font-semibold transition-all duration-200 hover:scale-105 hover:shadow-lg"
                style={{ 
                  background: PALETTE.inchworm, 
                  color: PALETTE.gunmetal 
                }}
              >
                Get Started
                <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
              </motion.div>

          {/* Pro Plan Card */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.15 }}
          >
          <Card className="w-full h-full border-2 hover:border-opacity-100 transition-all duration-300 flex flex-col" 
            style={{ borderColor: PALETTE.inchworm }}>
            <CardContent className="p-8 flex flex-col flex-1">
              {/* Plan Header */}
              <div className="text-center mb-6">
                <div className="flex items-center justify-center gap-2">
                  <h2 className="text-2xl font-bold mb-1" style={{ color: PALETTE.gunmetal }}>
                    Pro
                  </h2>
                  <span 
                    className="px-3 py-1 rounded-full text-xs font-semibold"
                    style={{ 
                      background: PALETTE.inchworm,
                      color: PALETTE.gunmetal
                    }}
                  >
                    Best Value
                  </span>
                </div>
              </div>

              {/* Price */}
              <div className="text-center mb-6">
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-5xl font-bold" style={{ color: PALETTE.gunmetal }}>
                    €69
                  </span>
                  <span className="text-lg" style={{ color: `${PALETTE.gunmetal}60` }}>
                    /month
                  </span>
                </div>
                <p className="text-sm mt-2" style={{ color: `${PALETTE.gunmetal}70` }}>
                  For growing agencies & mid-sized marketing teams
                </p>
              </div>

              {/* Features */}
              <ul className="space-y-4 mb-8 flex-1">
                <li className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5"
                    style={{ background: PALETTE.inchworm }}>
                    <Check className="w-3 h-3" style={{ color: PALETTE.gunmetal }} strokeWidth={3} />
                  </div>
                  <span style={{ color: PALETTE.gunmetal }}>
                    <strong>100</strong> video checks per month
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5"
                    style={{ background: PALETTE.inchworm }}>
                    <Check className="w-3 h-3" style={{ color: PALETTE.gunmetal }} strokeWidth={3} />
                  </div>
                  <span style={{ color: PALETTE.gunmetal }}>
                    Unlimited campaigns
                  </span>
                </li>                
                <li className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5"
                    style={{ background: PALETTE.inchworm }}>
                    <Check className="w-3 h-3" style={{ color: PALETTE.gunmetal }} strokeWidth={3} />
                  </div>
                  <span style={{ color: PALETTE.gunmetal }}>
                    Unlimited shareable submission links
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5"
                    style={{ background: PALETTE.inchworm }}>
                    <Check className="w-3 h-3" style={{ color: PALETTE.gunmetal }} strokeWidth={3} />
                  </div>
                  <span style={{ color: PALETTE.gunmetal }}>
                    Guided onboarding 
                  </span>
                </li>             
              </ul>

              {/* CTA Button */}
              <Button
                onClick={() => openModal(false)}
                className="w-full h-12 rounded-full text-base font-semibold transition-all duration-200 hover:scale-105 hover:shadow-lg"
                style={{ 
                  background: PALETTE.inchworm, 
                  color: PALETTE.gunmetal 
                }}
              >
                Get Started
                <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
                </CardContent>
              </Card>
          </motion.div>

              {/* Custom Plan Card */}
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
              <Card className="w-full h-full border-2 hover:border-opacity-100 transition-all duration-300 flex flex-col" 
                style={{ borderColor: `${PALETTE.americanSilver}60` }}>
                <CardContent className="p-8 flex flex-col flex-1">
                  {/* Plan Header */}
                  <div className="text-center mb-6">
                    <h2 className="text-2xl font-bold mb-1" style={{ color: PALETTE.gunmetal }}>
                      Custom
                    </h2>                
                  </div>                  

                  {/* Price */}
                  <div className="text-center mb-6">
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-5xl font-bold" style={{ color: PALETTE.gunmetal }}>
                        Contact us
                      </span>
                    </div>
                    <p className="text-sm mt-2" style={{ color: `${PALETTE.gunmetal}70` }}>
                      For large agencies & enterprise brands
                    </p>
                  </div>

                  {/* Features */}
                  <ul className="space-y-4 mb-8 flex-1">
                    <li className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5"
                        style={{ background: PALETTE.inchworm }}>
                        <Check className="w-3 h-3" style={{ color: PALETTE.gunmetal }} strokeWidth={3} />
                      </div>
                      <span style={{ color: PALETTE.gunmetal }}>
                        Custom built packages
                      </span>
                    </li>  
                    <li className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5"
                      style={{ background: PALETTE.inchworm }}>
                      <Check className="w-3 h-3" style={{ color: PALETTE.gunmetal }} strokeWidth={3} />
                    </div>
                    <span style={{ color: PALETTE.gunmetal }}>
                      Custom branding
                    </span>
                  </li>
                  </ul>

                  {/* CTA Button */}
                  <Button
                    onClick={() => window.location.href = 'mailto:info@brandcomply.ai'}
                    className="w-full h-12 rounded-full text-base font-semibold transition-all duration-200 hover:scale-105 hover:shadow-lg"
                    style={{ 
                      background: PALETTE.inchworm, 
                      color: PALETTE.gunmetal 
                    }}
                  >
                    Talk to us
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
              </motion.div>
            </div>
          </div>
        </section>
      
        {/* FAQ */}
        <section id="faq" className="py-16" style={{ background: PALETTE.brightSnow }}>
          <div className="max-w-4xl mx-auto px-6">
            <motion.div
              className="text-center mb-12"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-5xl font-bold mb-4" style={{ color: PALETTE.gunmetal }}>Frequently asked questions</h2>
            </motion.div>

            <div className="space-y-2">
              {[
                {
                  question: "What is BrandComply?",
                  answer: "BrandComply is a tool that helps marketing agencies and marketing teams to streamline content approvals for UGC and Influencer video marketing campaigns. It enables UGC creators and influencers to self-check their video drafts against campaign briefs before submitting, helping them deliver compliant content faster."
                },
                {
                  question: "How does BrandComply work?",
                  answer: "BrandComply uses AI to let influencers and UGC creators receive instant and private feedback on how their videos match the campaign brief before submitting. So they can then re-check their video until they are ready to submit. Your team can then review the submissions in one dashboard and see, via the BrandComply score, how well each video matches the brief."
                },
                {
                  question: "How do creators self-check their videos against the brief?",
                  answer: "Our AI learns the requirements of the brief. When a creator uploads a video via the submission link our AI checks how well the video meets these requirements. The creators receive instant objective and private feedback on how their video meets the brief. They can re-check their video until they are ready to submit."
                },
                {
                  question: "Does BrandComply evaluate the creativity or style of the creator's video?",
                  answer: "No, BrandComply does not evaluate the creativity or style of the creator's video. Our system is objective. Your creativity, storytelling, and style are left entirely to the human review of the agency/brand."
                },
                {
                  question: "Can BrandComply check if advertising content is correctly labeled and disclosed in the video?",
                  answer: "Yes, BrandComply can check if advertising content is correctly labeled and disclosed in the video if you configure your campaign brief accordingly."
                },
                {
                  question: "Does BrandComply guarantee legal compliance?",
                  answer: "No, BrandComply does not guarantee legal compliance. While BrandComply can check videos for certain legal requirements such as disclosures or text overlays, we do not guarantee legal compliance."
                },
                {
                  question: "Who is BrandComply for?",
                  answer: "BrandComply is for marketing agencies and marketing teams who want to streamline content approvals for UGC and Influencer video marketing campaigns, saving time and effort, increasing brand safety and legal compliance, and increasing consistency across UGC and Influencer campaigns."
                },
                {
                  question: "What video file formats do you support?",
                  answer: "We support the following video format MIME types: video/mp4, video/mpeg, video/mov, video/avi, video/x-flv, video/mpg, video/webm, video/wmv, video/3gpp."
                },                
                {
                  question: "What is the maximum size of a video I can upload?",
                  answer: "The maximum size of a video is 500MB."
                },
                {
                  question: "What is the maximum length of a video I can upload?",
                  answer: "There is no maximum length for videos. However, larger files may take longer to analyze."
                },
                {
                  question: "How many videos can I upload at once?",
                  answer: "As of now, you can upload 1 video at once."
                },
                {
                  question: "What video languages do you support?",
                  answer: "We support the following core languages: English, Spanish, Portuguese, French, German, Italian, Dutch, Russian, Polish, Swedish, Danish, Norwegian, Finnish, Greek, Czech, Japanese, Korean, Chinese (Simplified/Traditional), Hindi, Indonesian, Thai, Vietnamese, Turkish, Arabic, Hebrew. And many more."
                },
                {
                  question: "How long does a video check take?",
                  answer: "Most video checks complete within 60 seconds, with larger files taking up to 90 seconds."
                },
                {
                  question: "What counts as a video check?",
                  answer:
                    "A video check is counted each time a creator uploads a video and BrandComply checks it against your campaign brief. If a creator re-uploads a video, that upload is checked again and counts as an additional video check. The dashboard shows only videos that are currently pending or have been finally submitted, so the number of video checks used can be higher than the number of videos visible in the dashboard.",
                },
                {
                  question: "What happens when I reach my limit?",
                  answer:
                    "Once you've used all your video checks for the month, new video submissions will be paused until your next billing cycle begins or you upgrade your plan. If you need more than 100 video checks, please contact us at info@brandcomply.ai.",
                },
                {
                  question: "Do I need a credit card to start?",
                  answer:
                    "Yes, you need a credit card to start.",
                }, 
                {
                  question: "Can I cancel my plan anytime?",
                  answer:
                    "Yes! You can cancel your plan at any time through the billing portal. You'll continue to have access until the end of your current billing period.",
                }, 
                {
                  question: "Is BrandComply available as an API?",
                  answer: "Yes, BrandComply is available as an API. Please contact us via mail (info@brandcomply.ai) if you are interested in using BrandComply as an API."
                },                
              ].map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                >
                <Card className="border" style={{ borderColor: `${PALETTE.americanSilver}30` }}>
                  <CardContent className="p-0">
                    <button
                      className="w-full p-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                      onClick={() => toggleFaqItem(index)}
                    >
                      <h3 className="text-base font-semibold" style={{ color: PALETTE.gunmetal }}>
                        {faq.question}
                      </h3>
                      <ChevronDown 
                        className={`w-4 h-4 transition-transform ${openFaqItems[index] ? 'rotate-180' : ''}`}
                        style={{ color: PALETTE.gunmetal }}
                      />
                    </button>
                    {openFaqItems[index] && (
                      <div className="px-4 pb-4">
                        <p className="text-sm" style={{ color: `${PALETTE.gunmetal}70` }}>{faq.answer}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
        </div>

        {/* Final CTA */}
        <section className="py-20 text-white" style={{ background: `linear-gradient(to bottom right, ${PALETTE.gunmetal}, ${PALETTE.gunmetal}90)` }}>
          <motion.div
            className="max-w-4xl mx-auto px-6 text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl font-bold mb-4">Your next creator campaign — without the revision chaos.</h2>
            <p className="text-lg text-white/70 mb-8">Still not sure? Paste a video URL above and see BrandComply in action.</p>
            <div className="flex items-center justify-center gap-4">
              <Button 
                className="font-semibold text-lg px-8 py-4 rounded-full hover:scale-105 transition-transform transition-colors duration-200"
                style={{ background: PALETTE.inchworm, color: PALETTE.gunmetal }}
                onClick={() => openModal(false)}
              >
                Start Free Trial
              </Button>
              <Button 
                className="font-semibold text-lg px-8 py-4 rounded-full hover:scale-105 transition-transform transition-colors duration-200"
                style={{ background: 'transparent', color: PALETTE.brightSnow, border: `2px solid ${PALETTE.brightSnow}` }}
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              >
                Try it free
              </Button>
            </div>
          </motion.div>
        </section>

        {/* Footer */}
        <footer className="py-12 text-white" style={{ background: PALETTE.gunmetal }}>
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-start gap-16">
              {/* Brand Info */}
              <div className="max-w-xs">
                <div 
                  className="flex items-center space-x-1 cursor-pointer"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                >
                  <img src="/brandcomply_logo.png" alt="Brand Logo" className="h-6 w-auto" />
                  <div className="text-xl font-bold" style={{ color: PALETTE.inchworm }}>
                    BrandComply
                  </div>
                </div>
                <p className="text-white/70 mt-4">
                  AI-powered content approval for Influencer and UGC video campaigns.  
                </p>
              </div>

              {/* Product */}
              <div>
                <h3 className="text-lg font-semibold mb-4" style={{ color: PALETTE.inchworm }}>
                  Product
                </h3>
                  <div className="space-y-3">
                    <button
                      onClick={() => {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                        setTimeout(() => {
                          const howItWorksSection = document.getElementById('how-it-works');
                          howItWorksSection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }, 100);
                      }}
                      className="block text-white/70 hover:text-white transition-colors text-left"
                    >
                      How it Works
                    </button>
                    <button
                      onClick={() => {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                        setTimeout(() => {
                          const pricingSection = document.getElementById('pricing');
                          pricingSection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }, 100);
                      }}
                      className="block text-white/70 hover:text-white transition-colors text-left"
                    >
                      Pricing
                    </button>
                    <button
                      onClick={() => navigate('/api')}
                      className="block text-white/70 hover:text-white transition-colors text-left"
                    >
                      API
                    </button>
                </div>
              </div>

              {/* Legal */}
              <div>
                <h3 className="text-lg font-semibold mb-4" style={{ color: PALETTE.inchworm }}>
                  Legal
                </h3>
                <div className="space-y-3">
                  <button
                    onClick={() => navigate('/terms-of-service')}
                    className="block text-white/70 hover:text-white transition-colors text-left"
                  >
                    Terms of Service
                  </button>
                  <button
                    onClick={() => navigate('/privacy-policy')}
                    className="block text-white/70 hover:text-white transition-colors text-left"
                  >
                    Privacy Policy
                  </button>
                  <button
                    onClick={() => navigate('/imprint')}
                    className="block text-white/70 hover:text-white transition-colors text-left"
                  >
                    Imprint
                  </button>
                </div>
              </div>
            </div>

            <div className="border-t border-white/20 mt-12 pt-8 text-center text-white/70">
              <p>&copy; 2026 BrandComply. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>

      {/* Login Modal */}
      {/* {showLoginModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <Card style={{ background: PALETTE.brightSnow }} className="w-full max-w-md p-8 relative">
            <button
              onClick={closeLoginModal}
              className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full"
            >
              <X className="w-5 h-5"style={{ color: PALETTE.gunmetal }} />
            </button>

            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold mb-2" style={{ color: PALETTE.gunmetal }}>
                {isLogin ? "Welcome Back" : "Create Account"}
              </h1>
              <p style={{ color: PALETTE.americanSilver }}>
                {isLogin
                  ? "Sign in to continue to BrandComply"
                  : "Sign up to get started with BrandComply"}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div>
                  <label className="text-sm font-medium block mb-1" style={{ color: PALETTE.gunmetal }}>
                    Full Name
                  </label>
                  <Input
                    type="text"
                    placeholder="Enter your full name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </div>
              )}

              <div>
                <label className="text-sm font-medium block mb-1" style={{ color: PALETTE.gunmetal }}>
                  Email Address
                </label>
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div>
                <label className="text-sm font-medium block mb-1" style={{ color: PALETTE.gunmetal }}>
                  Password
                </label>
                <Input
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>

              {error && (
                <div className={`text-sm text-center`} style={{ color: error.includes("successful") ? PALETTE.inchworm : PALETTE.orange }}>
                  {error}
                </div>
              )}

              <Button
                type="submit"
                className="w-full"
                style={{ background: PALETTE.orange, color: '#fff' }}
                disabled={isLoading}
              >
                {isLoading
                  ? "Please wait..."
                  : isLogin
                  ? "Sign In"
                  : "Create Account"}
              </Button>

              <div className="text-center text-sm" style={{ color: PALETTE.americanSilver }}>
                {isLogin ? "Don't have an account? " : "Already have an account? "}
                <button
                  type="button"
                  onClick={() => {
                    setIsLogin(!isLogin);
                    setError("");
                  }}
                  style={{ color: PALETTE.paleViolet, fontWeight: 500, textDecoration: 'underline' }}
                >
                  {isLogin ? "Sign up" : "Sign in"}
                </button>
              </div>
            </form>
          </Card>
        </div>
      )} */}
    </>
  );
};

export default Auth;